import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import { Box } from "@mui/material";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { NotificationFeedPreference } from "@storyboard/web-client";
import BellIconOn from "@/components/Icons/BellIconOn.tsx";
import ListIcon from "@/components/Icons/ListIcon";
import UserGroup from "@/components/Icons/UserGroup.tsx";
import ChannelContextItem from "@/components/WorkspaceChannelList/ChannelContextItem.tsx";
import { ActionContext } from "@/models/ActionsProvider";
import {
  subscribeToFeed,
  subscribeToFeedGroup,
  unsubscribeFromFeed,
  unsubscribeFromFeedGroup,
} from "@/models/actions/clientActions";
import { LiveQueryContext } from "@/models/LiveQueriesProvider";
import { ChannelList, fetchAllGroupedChannelsQuery, fetchAllNonGroupedChannelsQuery } from "@/models/repositories";
import { CurrentFeedContext } from "@/models/StateProviders/currentFeedProvider";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { TrackingContext } from "@/models/TrackingStateProvider";
import BellIconOff from "../Icons/BellIconOff";
import MdiCloseThick from "../Icons/MdiCloseThick";

interface Props {
  channel: ChannelList;
  workspaceMembershipId: string;
  handleClose: () => void;
  channelUrl: string;
  readOnlyMode: boolean;
}

export default function LinkContextMenu({
  channel,
  workspaceMembershipId,
  handleClose,
  channelUrl,
  readOnlyMode,
}: Props) {
  const navigate = useNavigate();

  const { ampli } = useContext(TrackingContext);
  const { fetchNextActiveChannel } = useContext(LiveQueryContext);
  const { accountEvent } = useContext(ActionContext);
  const { currentWorkspaceId: workspaceId } = useContext(WorkspaceContext);
  const { currentFeedId: feedId } = useContext(CurrentFeedContext);
  const [disabled, setDisabled] = useState<boolean>(false);

  const CloseThickIcon = () => <MdiCloseThick fontSize="25" color="#fc2324" />;
  const LocalUserGroupIcon = () => <UserGroup fontSize="20" color="#6da7d7" />;

  const LocalBellIconOff = () => <BellIconOff fontSize="18" />;

  const LocalBellIconOn = () => <BellIconOn fontSize="18" />;

  const markChannelAsRead = async () => {
    handleClose();
    accountEvent("Marked Feed as Read", {
      feedId: channel.id,
    });
    setTimeout(() => setDisabled(() => false), 1000);
  };

  const subscribeChannel = async (id: string, redirect: boolean, preference: NotificationFeedPreference) => {
    setDisabled(() => true);
    handleClose();
    await subscribeToFeed({ workspaceId, feedId: id, workspaceMembershipId, preference });
    setTimeout(() => setDisabled(() => false), 1000);
    if (feedId !== id && redirect) {
      navigate(channelUrl);
    }
  };

  const unsubscribeChannel = async () => {
    setDisabled(() => true);
    handleClose();
    await unsubscribeFromFeed({
      workspaceId,
      feedId: channel.id,
      workspaceMembershipId,
    });
    const url = await fetchNextActiveChannel();
    const leavingActiveChannel = channel.id === feedId;
    if (leavingActiveChannel) {
      navigate(url);
    }
    setTimeout(() => setDisabled(() => false), 1000);
  };

  const subscribeGroup = async (preference: NotificationFeedPreference) => {
    setDisabled(() => true);
    handleClose();
    ampli.groupJoin({ workspaceId });
    await subscribeToFeedGroup({
      workspaceId,
      feedGroupId: channel.groupId,
      workspaceMembershipId,
      preference,
    });
    setTimeout(() => setDisabled(() => false), 1000);
  };

  const unsubscribeGroup = async () => {
    setDisabled(() => true);
    ampli.groupLeave({ workspaceId });
    handleClose();
    await unsubscribeFromFeedGroup({
      workspaceId,
      feedGroupId: channel.groupId,
      workspaceMembershipId,
    });
    const url = await fetchNextActiveChannel();
    setTimeout(() => setDisabled(() => false), 1000);
    if (url) {
      navigate(url);
    }
  };

  /**
   * Mark As Read
   */
  const markItemsAsRead = async (ids: string[]) => {
    setDisabled(() => true);
    handleClose();
    if (ids?.length > 0) {
      for (const feedId of ids) {
        accountEvent("Marked Feed as Read", {
          feedId,
        });
      }
    }
    setTimeout(() => setDisabled(() => false), 1000);
  };
  const markAllInGroupAsRead = async () => {
    const groups = await fetchAllGroupedChannelsQuery({
      workspaceMembershipId,
      groupId: channel.groupId,
    });
    const mapGroups = groups?.map((f) => f?.id);
    await markItemsAsRead(mapGroups);
  };
  const markAllChannelsAsRead = async () => {
    const feeds = await fetchAllNonGroupedChannelsQuery({ workspaceMembershipId });
    const mapFeeds = feeds?.map((f) => f?.id);
    await markItemsAsRead(mapFeeds);
  };

  /**
   * End Mark As Read Options
   */
  const markAsReadOption = !readOnlyMode &&
    channel.unread && {
      label: "Mark As Read",
      action: () => markChannelAsRead(),
      icon: <DraftsOutlinedIcon />,
    };

  const markAllInGroupOption = !readOnlyMode &&
    channel.unread &&
    channel.groupId && {
      label: "Mark All in Group as Read",
      action: () => markAllInGroupAsRead(),
      icon: <ListIcon />,
    };

  const markAllAsReadOption = !readOnlyMode &&
    channel.unread &&
    !channel.groupId && {
      label: "Mark All as Read",
      action: async () => markAllChannelsAsRead(),
      icon: <ListIcon />,
    };

  /**
   * Aliased Channels
   */
  const notifyDriversNotSpecialNotGroupedAliasChannel = !channel.subscribed &&
    channel.aliasChannel &&
    !channel.groupId && {
      label: "Notify me of new driver messages",
      action: () => subscribeChannel(channel.id, false, "onlyAliasOwner"),
      icon: <LocalBellIconOn />,
    };

  const turnOffDriversNotSpecialNotGroupedAliasChannel = channel.subscribed &&
    channel.aliasChannel &&
    !channel.groupId && {
      label: "Turn off notifications for new driver messages",
      action: () => unsubscribeChannel(),
      icon: <CloseThickIcon />,
    };

  const notifyDriversNotSpecialGroupedAliasChannel = !channel.subscribed &&
    channel.aliasChannel &&
    channel.groupId && {
      label: "Notify me of new driver messages for this group",
      action: () => subscribeGroup("onlyAliasOwner"),
      icon: <LocalUserGroupIcon />,
    };

  const turnOffDriversNotSpecialGroupedAliasChannel = channel.subscribed &&
    channel.aliasChannel &&
    channel.groupId && {
      label: "Turn off notifications for new driver messages in this group",
      action: () => unsubscribeGroup(),
      icon: <CloseThickIcon />,
    };

  const turnOnNotificationsForAliasChannelInAGroup = channel.aliasChannel &&
    channel.groupId &&
    !channel.subscribed && {
      label: "Notify me of new driver messages",
      action: () => subscribeChannel(channel.id, false, "onlyAliasOwner"),
      icon: <LocalBellIconOn />,
    };

  const turnOffNotificationsForAliasChannelInAGroup = channel.aliasChannel &&
    channel.groupId &&
    channel.subscribed && {
      label: "Turn off notifications for new driver messages",
      action: () => unsubscribeChannel(),
      icon: <LocalBellIconOff />,
    };

  /**
   * Non-Aliased Channels
   */
  const notifyDriversNotSpecialNotGroupedNonAliasChannel = !channel.subscribed &&
    !channel.aliasChannel &&
    !channel.groupId && {
      label: "Notify me of new messages",
      action: () => subscribeChannel(channel.id, false, "all"),
      icon: <LocalBellIconOn />,
    };

  const turnOffDriversNotSpecialNotGroupedNonAliasChannel = channel.subscribed &&
    !channel.aliasChannel &&
    !channel.groupId && {
      label: "Turn off notifications for new messages",
      action: () => unsubscribeChannel(),
      icon: <CloseThickIcon />,
    };

  const notifyDriversNotSpecialGroupedNonAliasChannel = !channel.subscribed &&
    !channel.aliasChannel &&
    channel.groupId && {
      label: "Notify me of new messages in this group",
      action: () => subscribeGroup("all"),
      icon: <LocalUserGroupIcon />,
    };

  const turnOffDriversNotSpecialGroupedNonAliasChannel = channel.subscribed &&
    !channel.aliasChannel &&
    channel.groupId && {
      label: "Turn off notifications for new messages in this group",
      action: () => unsubscribeGroup(),
      icon: <CloseThickIcon />,
    };

  const subscribeToNotificationsForNonAliasChannelInAGroup = !channel.subscribed &&
    !channel.aliasChannel &&
    channel.groupId && {
      label: "Subscribe to notifications",
      action: () => subscribeChannel(channel.id, false, "all"),
      icon: <LocalBellIconOn />,
    };

  const turnOffNotificationsForNonAliasChannelInAGroup = channel.subscribed &&
    !channel.aliasChannel &&
    channel.groupId && {
      label: "Turn off notifications for this channel",
      action: () => unsubscribeChannel(),
      icon: <LocalBellIconOff />,
    };

  /**
   * Add all the items to an array and filter out the undefined ones
   */
  const filteredAll = [
    markAsReadOption,
    markAllAsReadOption,
    markAllInGroupOption,
    notifyDriversNotSpecialNotGroupedAliasChannel,
    turnOffDriversNotSpecialNotGroupedAliasChannel,
    notifyDriversNotSpecialGroupedAliasChannel,
    turnOffDriversNotSpecialGroupedAliasChannel,
    notifyDriversNotSpecialNotGroupedNonAliasChannel,
    turnOffDriversNotSpecialNotGroupedNonAliasChannel,
    notifyDriversNotSpecialGroupedNonAliasChannel,
    turnOffDriversNotSpecialGroupedNonAliasChannel,
    subscribeToNotificationsForNonAliasChannelInAGroup,
    turnOffNotificationsForNonAliasChannelInAGroup,
    turnOnNotificationsForAliasChannelInAGroup,
    turnOffNotificationsForAliasChannelInAGroup,
  ].filter((c) => c);

  if (filteredAll?.length === 0) {
    return <Box sx={{ fontSize: "12px" }}>No Options Available</Box>;
  }

  return (
    <>
      {filteredAll?.map((option) => (
        <ChannelContextItem
          key={option.label}
          text={option.label}
          disabled={disabled}
          label={`Change Channel Subscription: ${option.label}`}
          channelContextAction={option.action}
          icon={option.icon}
        />
      ))}
    </>
  );
}
